import { useRef, useState } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';
import { ArrowRight, CheckCircle2, MapPin } from 'lucide-react';
import { MaskedLine } from './Reveal';
import { waLink } from '../data/site';

const Plate = () => {
  const ref = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 160, damping: 18 });
  const sry = useSpring(ry, { stiffness: 160, damping: 18 });

  const onMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    ry.set(px * 16);
    rx.set(-py * 16);
  };
  const onLeave = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
    <motion.div
      ref={ref}
      data-testid="hero-plate-card"
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ rotateX: srx, rotateY: sry, transformStyle: 'preserve-3d' }}
      initial={{ opacity: 0, y: 60, rotateZ: -6 }}
      animate={{ opacity: 1, y: 0, rotateZ: -4 }}
      transition={{ duration: 1.2, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="w-[280px] sm:w-[340px] rounded-xl overflow-hidden plate-shadow cursor-pointer select-none"
    >
      <div className="plate-band flex items-center justify-between px-4 py-2">
        <span className="text-white/90 text-[10px] font-mono tracking-[0.35em] uppercase">
          Brasil
        </span>
        <span className="text-white/60 text-[10px] font-mono tracking-widest">
          MERCOSUL
        </span>
      </div>
      <div className="plate-body py-4 sm:py-5 text-center">
        <span className="font-mono2 text-4xl sm:text-5xl font-bold text-[#0B0C10] tracking-[0.12em]">
          DVY·2A26
        </span>
      </div>
    </motion.div>
  );
};

export const Hero = () => {
  const [name, setName] = useState('');
  const [plate, setPlate] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');

  const submit = (e) => {
    e.preventDefault();
    if (plate.trim().length < 7) {
      setError('Informe a placa completa (ex: ABC1D23).');
      return;
    }
    setError('');
    const msg = [
      'Olá Davy! Vim pelo site e quero uma consulta gratuita.',
      name.trim() ? `Nome: ${name.trim()}.` : null,
      `Placa: ${plate.trim().toUpperCase()}.`,
      phone.trim() ? `Telefone: ${phone.trim()}.` : null,
    ]
      .filter(Boolean)
      .join(' ');
    window.open(waLink(msg), '_blank', 'noopener,noreferrer');
  };

  return (
    <section
      id="topo"
      data-testid="hero-section"
      className="relative bg-white overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-36 sm:pt-44 pb-20 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <h1
            data-testid="hero-title"
            className="font-display font-extrabold tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl text-slate-900"
          >
            <MaskedLine delay={0.25}>
              <span className="text-blue-600">Consulte sua placa grátis</span>
            </MaskedLine>
            <MaskedLine delay={0.4}>e regularize seu veículo</MaskedLine>
            <MaskedLine delay={0.55}>sem sair de casa.</MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.8 }}
            className="mt-5 max-w-md text-base sm:text-lg text-slate-500 leading-relaxed"
          >
            Transferência, licenciamento, multas, débitos e alvará de
            liberação — o Davy resolve tudo no DETRAN-MG e te atualiza pelo
            WhatsApp.
          </motion.p>

          <motion.form
            data-testid="hero-consult-form"
            onSubmit={submit}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95 }}
            className="mt-8 max-w-md rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-[0_25px_60px_-25px_rgba(15,23,42,0.25)] space-y-3"
          >
            <input
              data-testid="hero-placa-input"
              type="text"
              value={plate}
              onChange={(e) => setPlate(e.target.value.toUpperCase())}
              placeholder="Placa do veículo"
              maxLength={8}
              className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3.5 font-mono text-sm tracking-[0.18em] text-slate-900 placeholder:font-sans placeholder:tracking-normal placeholder:text-slate-400 focus:outline-none focus:border-blue-500 transition-colors"
            />
            <input
              data-testid="hero-name-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Seu nome"
              className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 transition-colors"
            />
            <input
              data-testid="hero-phone-input"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Seu telefone"
              className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 transition-colors"
            />
            {error && (
              <p
                data-testid="hero-form-error"
                className="text-xs text-red-500 font-medium"
              >
                {error}
              </p>
            )}
            <button
              data-testid="hero-consult-submit"
              type="submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl px-6 py-4 text-sm sm:text-base transition-all duration-300 hover:shadow-[0_15px_40px_-10px_rgba(37,99,235,0.5)]"
            >
              Consultar Agora
              <ArrowRight size={18} />
            </button>
          </motion.form>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="mt-7 flex flex-wrap gap-x-7 gap-y-2.5"
          >
            {[
              'Atendimento rápido pelo WhatsApp',
              'Processo transparente, do início ao fim',
              'Cobertura em toda Minas Gerais',
            ].map((text) => (
              <span
                key={text}
                className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-slate-500"
              >
                <CheckCircle2 size={15} className="text-blue-600" />
                {text}
              </span>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="relative hidden lg:block"
        >
          <div className="relative rounded-3xl bg-slate-100 p-10 overflow-hidden min-h-[540px] flex flex-col">
            <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full border-[16px] border-blue-600/10" />
            <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full border-[16px] border-blue-600/10" />

            <div className="relative">
              <h3 className="font-display text-3xl font-extrabold text-slate-900 leading-tight">
                Agora é a hora,
                <br />
                <span className="text-blue-600">Minas Gerais!</span>
              </h3>
              <p className="mt-3 text-sm text-slate-500 max-w-xs leading-relaxed">
                Do emplacamento ao alvará de liberação: você manda mensagem,
                eu cuido da burocracia no DETRAN-MG.
              </p>
            </div>

            <div
              className="relative flex-1 flex items-center justify-center py-8"
              style={{ perspective: 900 }}
            >
              <Plate />
            </div>

            <div className="relative flex flex-wrap gap-2">
              {['Betim', 'Contagem', 'Belo Horizonte', '+ toda MG'].map(
                (city, i) => (
                  <span
                    key={city}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold border ${
                      i < 3
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-slate-600 border-slate-200'
                    }`}
                  >
                    {i < 3 && <MapPin size={12} />}
                    {city}
                  </span>
                ),
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
